<script lang="ts">
  import type { BulkTransferResultItem } from "../../lib/bulkTransfer";

  export let open = false;
  export let progress = 0;
  export let processed = 0;
  export let total = 0;
  export let completed = 0;
  export let failed = 0;
  export let status: "starting" | "running" | "completed" | "failed" = "running";
  export let message = "Please wait while records are being transferred to mbdcb140.";
  export let results: BulkTransferResultItem[] = [];

  $: sortedResults = [...(results || [])].sort((a, b) => Number(a.id) - Number(b.id));

  const handleClose = () => {
    open = false;
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  function handleKeyDown(event: KeyboardEvent) {
    if (open && event.key === "Escape") {
      handleClose();
    }
  }
</script>

<svelte:window on:keydown={handleKeyDown} />

{#if open}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm transition-opacity duration-300"
  >
  <div class="w-11/12 max-w-lg rounded-xl bg-white p-6 shadow-2xl transition-all max-h-[90vh] flex flex-col">
    <div class="mb-4 flex items-center justify-between shrink-0">
      <div class="flex items-center space-x-3">
        {#if status === "completed"}
          <span class="flex h-9 w-9 items-center justify-center rounded-full bg-green-100 text-green-600 shrink-0">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </span>
        {:else if status === "failed"}
          <span class="flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        {:else}
          <span class="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-blue-600 animate-pulse shrink-0">
            <svg class="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
        {/if}
        <div>
          <h3 class="text-lg font-semibold text-gray-900">
            {#if status === "completed"}
              Transfer to 140 Complete
            {:else if status === "failed"}
              Transfer Encountered an Issue
            {:else}
              Transferring to 140...
            {/if}
          </h3>
          <p class="text-xs text-gray-500">
            {#if total > 0}
              {processed} of {total} records processed ({Math.floor(progress)}%)
            {:else}
              Queuing transfer...
            {/if}
          </p>
        </div>
      </div>

      <button
        type="button"
        on:click={handleClose}
        class="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors shrink-0"
        title="Close modal"
      >
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- GRUIM styled Progress Bar -->
    <div class="my-3 shrink-0">
      <div class="w-full bg-gray-200 shadow-inner rounded-full overflow-hidden h-4">
        <div
          class="bg-blue-600 h-full text-[10px] leading-4 font-semibold text-white text-center transition-all duration-300 ease-out rounded-full"
          style="width: {Math.max(progress, 0)}%"
        >
          {#if progress >= 8}
            {`${Math.floor(progress)}%`}
          {/if}
        </div>
      </div>
    </div>

    <!-- Counters & Subtitle -->
    <div class="flex items-center justify-between text-xs text-gray-600 shrink-0">
      <div class="truncate max-w-[320px]">
        {message}
      </div>
      {#if total > 0}
        <div class="flex items-center space-x-2 font-medium shrink-0">
          {#if completed > 0}
            <span class="text-green-600">✓ {completed}</span>
          {/if}
          {#if failed > 0}
            <span class="text-red-500">✗ {failed}</span>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Transfer Records List -->
    {#if results && results.length > 0}
      <div class="mt-4 border-t border-gray-100 pt-3 flex flex-col min-h-0 flex-1">
        <div class="flex items-center justify-between mb-2 shrink-0">
          <span class="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Transferred Records ({results.length})
          </span>
          <span class="text-[11px] text-gray-400">
            {results.filter((r) => r.status === "completed").length} transferred · {results.filter((r) => r.status === "failed").length} failed
          </span>
        </div>

        <div class="overflow-y-auto max-h-52 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-gray-50/50">
          {#each sortedResults as item (item.id)}
            <div class="flex items-center justify-between px-3 py-2 text-xs hover:bg-white transition-colors">
              <div class="flex items-center space-x-2 min-w-0">
                <span class="font-mono font-semibold text-gray-800 shrink-0">#{item.id}</span>
                {#if item.status === "failed"}
                  <span class="text-[11px] text-red-500 truncate max-w-[200px]" title={item.error || "Failed"}>
                    {item.error || "Failed"}
                  </span>
                {:else if item.targetPaybackId}
                  <span class="text-[11px] text-gray-500 truncate">
                    → target #{item.targetPaybackId}
                  </span>
                {/if}
              </div>
              <div class="shrink-0 ml-2">
                {#if item.status === "completed"}
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-800 border border-green-200">
                    <svg class="w-3 h-3 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                    TRANSFERRED
                  </span>
                {:else if item.status === "failed"}
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800 border border-red-200">
                    FAILED
                  </span>
                {:else}
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                    PENDING
                  </span>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Close button for completed or failed states -->
    {#if status === "completed" || status === "failed"}
      <div class="mt-4 shrink-0">
        <button
          type="button"
          on:click={handleClose}
          class="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          Close
        </button>
      </div>
    {/if}
  </div>
</div>
{/if}
